import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Trash2, Plus, X, CalendarDays } from "lucide-react";
import { seasonLabel } from "@/components/group/GroupGamePlayer";
import { GROUP_TYPES } from "@/components/group/GroupGamePlayer";

const FIELD_LABELS = { text: "Phrase (___ = mots cachés)", answer: "Réponse", kind: "Type", label: "Nom", title: "Titre", questions: "Questions (une par ligne)",
  question: "Question", options: "Réponses (une par ligne)", clues: "Indices (un par ligne)", categories: "Catégories (une par ligne)",
  letters: "Lettres (une par ligne)", word: "Mot", hint: "Indice" };

function ItemEditor({ item, onChange, onRemove }) {
  return (
    <div className="relative rounded-xl border border-gray-200 p-3 space-y-2">
      <button onClick={onRemove} className="absolute top-2 right-2 text-gray-400 hover:text-rose-600" aria-label="Retirer"><X className="w-4 h-4" /></button>
      {item.image_url && <img src={item.image_url} alt="" loading="lazy" decoding="async" className="h-20 rounded-lg object-cover" />}
      {item.audio_url && <audio controls src={item.audio_url} className="h-10" />}
      {Object.entries(item).filter(([k]) => k !== "image_url" && k !== "audio_url").map(([k, v]) => (
        <div key={k}>
          <p className="text-xs font-semibold text-gray-600">{FIELD_LABELS[k] || k}{k === "answer" && Array.isArray(item.options) ? " (n° de la bonne réponse, à partir de 0)" : ""}</p>
          {Array.isArray(v) ? (
            <Textarea rows={Math.min(6, v.length + 1)} value={v.join("\n")} onChange={(e) => onChange({ ...item, [k]: e.target.value.split("\n") })} />
          ) : typeof v === "number" ? (
            <Input type="number" value={v} onChange={(e) => onChange({ ...item, [k]: Number(e.target.value) })} />
          ) : (
            <Input value={v} onChange={(e) => onChange({ ...item, [k]: e.target.value })} />
          )}
        </div>
      ))}
    </div>
  );
}

function EditDialog({ game, onClose, onSaved }) {
  const [g, setG] = useState(game);
  const [busy, setBusy] = useState(false);
  const setItem = (k, it) => setG((x) => ({ ...x, items: x.items.map((o, j) => (j === k ? it : o)) }));
  const save = async () => {
    setBusy(true);
    const items = g.items.map((it) => Object.fromEntries(Object.entries(it).map(([k, v]) => [k, Array.isArray(v) ? v.map((s) => s.trim()).filter(Boolean) : v])));
    try { await api.put(`/admin/group-games/${g.id}`, { title: g.title, description: g.description, active: g.active, items }); toast.success("Jeu de groupe enregistré."); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible."); } finally { setBusy(false); }
  };
  const blank = () => Object.fromEntries(Object.entries(g.items[0] || {}).filter(([k]) => k !== "image_url" && k !== "audio_url").map(([k, v]) => [k, Array.isArray(v) ? [] : typeof v === "number" ? 0 : ""]));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="gg-edit-dialog">
        <DialogHeader><DialogTitle>{GROUP_TYPES[g.type]?.label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={g.title} onChange={(e) => setG({ ...g, title: e.target.value })} data-testid="gg-edit-title" />
          <Textarea rows={2} value={g.description} onChange={(e) => setG({ ...g, description: e.target.value })} />
          <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={g.active} onCheckedChange={(v) => setG({ ...g, active: v })} data-testid="gg-edit-active" /> Publié</label>
          {g.items.map((it, k) => <ItemEditor key={k} item={it} onChange={(n) => setItem(k, n)} onRemove={() => setG({ ...g, items: g.items.filter((_, j) => j !== k) })} />)}
          {g.type !== "petit_bac" && g.type !== "loto" && g.type !== "photo_memories" && (
            <Button variant="outline" onClick={() => setG({ ...g, items: [...g.items, blank()] })}><Plus className="w-4 h-4 mr-1" /> Ajouter un élément</Button>
          )}
          <Button onClick={save} disabled={busy} className="w-full" data-testid="gg-edit-save">Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function GroupGamesAdmin() {
  const [games, setGames] = useState([]);
  const [editId, setEditId] = useState(null);
  const [pending, setPending] = useState("");
  const edit = games.find((g) => g.id === editId);
  const load = useCallback(() => api.get("/admin/group-games").then((r) => setGames(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (g) => {
    const active = !g.active;
    setPending(g.id);
    setGames((list) => list.map((x) => (x.id === g.id ? { ...x, active } : x)));
    try {
      await api.put(`/admin/group-games/${g.id}`, { title: g.title, description: g.description, active, items: g.items });
      toast.success(active ? "Jeu publié." : "Jeu masqué.");
    } catch { toast.error("Modification impossible."); load(); }
    finally { setPending(""); }
  };
  const makeSeason = async () => {
    try {
      const { data } = await api.post("/admin/season-games");
      toast.success(data.started ? `${data.started} jeu(x) de saison en préparation : à relire ici dans quelques minutes.` : "Les jeux de saison de ce mois existent déjà.");
      load();
    } catch { toast.error("Création impossible pour le moment."); }
  };
  const remove = async (g) => { if (window.confirm(`Supprimer « ${g.title} » ?`)) { await api.delete(`/admin/group-games/${g.id}`); load(); } };
  return (
    <Card className="rounded-2xl mt-6" data-testid="group-games-admin">
      <CardContent className="p-6">
        <h3 className="font-serif-display text-2xl">Jeux de groupe</h3>
        <p className="text-sm text-gray-500 mb-4">Créés par Gemini et masqués : relisez, modifiez puis publiez. {games.filter((g) => g.active).length}/{games.length} publiés.</p>
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="gg-season-box">
          <CalendarDays className="w-5 h-5 text-amber-700" />
          <p className="flex-1 text-sm text-amber-900">Le 1er de chaque mois, Gemini prépare 3 jeux de saison à imprimer (devinettes, petit bac, mot qui manque). Ils restent masqués jusqu'à votre relecture.</p>
          <Button variant="outline" size="sm" onClick={makeSeason} data-testid="gg-season-create">Créer ceux de ce mois</Button>
        </div>
        <div className="space-y-2">
          {games.map((g) => (
            <div key={g.id} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0" data-testid={`gg-admin-row-${g.id}`}>
              <span className="text-xs font-bold px-2 py-1 rounded-full bg-gray-100 shrink-0" style={{ color: GROUP_TYPES[g.type]?.color }}>{GROUP_TYPES[g.type]?.label}</span>
              <span className="flex-1 font-semibold text-gray-800 truncate">{g.title} <span className="text-gray-400 font-normal">· {g.items.length} élément{g.items.length > 1 ? "s" : ""}</span>
                {g.season && <span className="ml-2 text-xs font-bold text-amber-700" data-testid={`gg-admin-season-${g.id}`}>Saison · {seasonLabel(g.season)}{g.status === "pending" ? " · en préparation" : g.status === "error" ? " · échec" : !g.active ? " · à relire" : ""}</span>}
                {g.generated_by === "theme" && <span className="ml-2 text-xs font-bold text-amber-700" data-testid={`gg-admin-theme-${g.id}`}>Demandé{g.created_by_name ? ` par ${g.created_by_name}` : ""}{g.status === "pending" ? " · en préparation" : g.status === "error" ? " · échec" : ""}</span>}
              </span>
              <Switch checked={g.active} disabled={pending === g.id || !g.items.length} onCheckedChange={() => toggle(g)} data-testid={`gg-admin-active-${g.id}`} />
              <Button size="icon" variant="ghost" disabled={pending === g.id} onClick={() => setEditId(g.id)} data-testid={`gg-admin-edit-${g.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(g)} data-testid={`gg-admin-delete-${g.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
            </div>
          ))}
        </div>
        {edit && <EditDialog key={edit.id} game={edit} onClose={() => setEditId(null)} onSaved={() => { setEditId(null); load(); }} />}
      </CardContent>
    </Card>
  );
}
